import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { RootState } from '../../../../../redux/store';

import { TableTooltip } from '../../../../components/general/table';
import { DateTooltip } from '../../../../components/general/dateTooltip';

import { Product } from '../../../../../shared/types/systemConfiguration/products';
import { asyncGetProductsFilters } from '../../../../../services/product.service';
import { isOnlyViewProfile } from '../../../../../shared/types/profiles';
import { ISelect } from '../../../../../shared/types/shared';

import { ProductListActions } from './actions';

export const useProductList = (filters?: Product) => {
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<Product[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] = useState<Product>({});

    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const columns: GridColDef<Product>[] = [];

    if (!isOnlyViewProfile(type)) {
        columns.push({
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <ProductListActions id={row.row.id || ''} />,
        });
    }

    columns.push(
        {
            field: 'code',
            headerName: 'Código',
            minWidth: 120,
            flex: 0.2,
            renderCell: (row) => (
                <TableTooltip value={`${row.row.code ?? ''}`} />
            ),
        },
        {
            field: 'name',
            headerName: 'Nombre',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => <TableTooltip value={row.row.name ?? ''} />,
        },
        {
            field: 'SfcCaptureUnit',
            headerName: 'Unidad de Captura SFC',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip
                    value={(row.row.sfcCaptureUnit as ISelect)?.name || ''}
                />
            ),
        },
        {
            field: 'SfcCaptureUnitNew',
            headerName: 'Unidad de Captura SFC Nuevo',
            minWidth: 120,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={(row.row.sfcCaptureUnitNew as ISelect)?.name || ''}
                />
            ),
        },
        {
            field: 'applyEscalations',
            headerName: 'Aplica Escalamiento',
            minWidth: 120,
            flex: 0.4,
            align: 'center',
            renderCell: (row) => <>{row.row.applyEscalations ? 'Si' : 'No'}</>,
        },
        {
            field: 'applyPqr',
            headerName: 'Aplica Pqr',
            minWidth: 120,
            flex: 0.2,
            align: 'center',
            renderCell: (row) => <>{row.row.applyPqr ? 'Si' : 'No'}</>,
        },
        {
            field: 'active',
            headerName: 'Activo',
            minWidth: 80,
            flex: 0.2,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <>{row.row.active ? 'Si' : 'No'}</>,
        },
        {
            field: 'created_at',
            headerName: 'Fecha de creación',
            minWidth: 120,
            flex: 0.6,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <DateTooltip date={row.row.created_at} />,
        }
    );

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        asyncGetProductsFilters(finalFilters, page, pageSize)
            .then((res) => {
                setRows(res.items);
                setTotal(res.length);
            })
            .finally(() => setLoading(false));
    }, [page, pageSize, finalFilters]);

    return {
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
    };
};
